import React from 'react';
import { TrendingUp, Package, Users, Database, Layers, Wrench, ChevronDown, ChevronRight, Plus, Minus, CheckCircle, AlertTriangle } from 'lucide-react';

const FinalReviewStep = ({ quoteData, items, totals, calculatePEPM }) => {
  // Category groups matching other review steps
  const categoryGroups = {
    platform: {
      label: 'Platform & Deployment',
      icon: Package,
      color: 'blue',
      categories: ['platform']
    },
    users: {
      label: 'User Licenses',
      icon: Users,
      color: 'indigo',
      categories: ['users']
    },
    erpConnectors: {
      label: 'ERP Connectors',
      icon: Database,
      color: 'purple',
      categories: ['erpConnectors']
    },
    products: {
      label: 'Products',
      icon: Layers,
      color: 'teal',
      categories: ['reportsEBS', 'reportsCloud', 'financialAnalytics', 'peopleAnalytics', 'glConnect', 'dataPipelines', 'utilities'],
      subCategories: {
        reports: {
          label: 'Reports',
          categories: ['reportsEBS', 'reportsCloud']
        },
        analytics: {
          label: 'Analytics',
          categories: ['financialAnalytics', 'peopleAnalytics']
        },
        pipelines: {
          label: 'Data Pipelines',
          categories: ['dataPipelines']
        },
        glUtilities: {
          label: 'GL Connect & Utilities',
          categories: ['glConnect', 'utilities']
        }
      }
    },
    services: {
      label: 'Services',
      icon: Wrench,
      color: 'orange',
      categories: ['services']
    }
  };

  const [expandedGroups, setExpandedGroups] = React.useState({
    platform: true,
    users: true,
    erpConnectors: true,
    products: true,
    services: true
  });

  const [expandedSubCategories, setExpandedSubCategories] = React.useState({
    reports: true,
    analytics: true,
    pipelines: true,
    glUtilities: true
  });

  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  const toggleSubCategory = (subKey) => {
    setExpandedSubCategories(prev => ({ ...prev, [subKey]: !prev[subKey] }));
  };

  // Group items by category groups
  const groupedItems = React.useMemo(() => {
    const groups = {};
    Object.keys(categoryGroups).forEach(groupKey => {
      groups[groupKey] = items.filter(item =>
        categoryGroups[groupKey].categories.includes(item.category)
      );
    });
    return groups;
  }, [items]);

  const getEffectiveDiscount = (item) => {
    const lineDiscount = quoteData.lineItemDiscounts?.[item.sku] || 0;
    const catDiscount = quoteData.categoryDiscounts?.[item.category] || 0;
    return lineDiscount > 0 ? lineDiscount : catDiscount;
  };

  const calculateItemDiscount = (item) => {
    const subtotal = item.listPrice * item.quantity;
    const discount = getEffectiveDiscount(item) / 100;
    return subtotal * discount;
  };

  const calculateItemTotal = (item) => {
    const subtotal = item.listPrice * item.quantity;
    return subtotal - calculateItemDiscount(item);
  };

  // Calculate group subtotals
  const getGroupListTotal = (groupKey) => {
    return groupedItems[groupKey].reduce((sum, item) => sum + (item.listPrice * item.quantity), 0);
  };

  const getGroupDiscountedTotal = (groupKey) => {
    return groupedItems[groupKey].reduce((sum, item) => sum + calculateItemTotal(item), 0);
  };

  const getGroupDiscount = (groupKey) => {
    return getGroupListTotal(groupKey) - getGroupDiscountedTotal(groupKey);
  };

  // Get items for a sub-category
  const getSubCategoryItems = (subCategories) => {
    return items.filter(item => subCategories.includes(item.category));
  };

  // Get sub-category totals
  const getSubCategoryListTotal = (subCategories) => {
    return getSubCategoryItems(subCategories).reduce((sum, item) => sum + (item.listPrice * item.quantity), 0);
  };

  const getSubCategoryDiscountedTotal = (subCategories) => {
    return getSubCategoryItems(subCategories).reduce((sum, item) => sum + calculateItemTotal(item), 0);
  };

  const colorClasses = {
    blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', icon: 'bg-blue-600' },
    indigo: { bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700', icon: 'bg-indigo-600' },
    purple: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', icon: 'bg-purple-600' },
    teal: { bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-700', icon: 'bg-teal-600' },
    orange: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', icon: 'bg-orange-600' },
  };

  // Get PEPM data to check if HCM deal
  const pepmData = calculatePEPM();
  const isHCMDeal = !!pepmData;
  const employeeCount = pepmData?.employeeCount || 0;

  // Calculate PEPM for a line item (using discounted price)
  const calculateItemPEPM = (item) => {
    if (!isHCMDeal || item.isOneTime || employeeCount === 0) return null;
    const itemTotal = calculateItemTotal(item);
    return (itemTotal / (employeeCount * 12)).toFixed(2);
  };

  // Calculate PEPM for a group (ARR items only, using discounted prices)
  const getGroupPEPM = (groupKey) => {
    if (!isHCMDeal || employeeCount === 0) return null;
    const arrItems = groupedItems[groupKey].filter(item => !item.isOneTime);
    const arrTotal = arrItems.reduce((sum, item) => sum + calculateItemTotal(item), 0);
    return (arrTotal / (employeeCount * 12)).toFixed(2);
  };

  // Calculate PEPM for a sub-category (ARR items only, using discounted prices)
  const getSubCategoryPEPM = (subCategories) => {
    if (!isHCMDeal || employeeCount === 0) return null;
    const arrItems = getSubCategoryItems(subCategories).filter(item => !item.isOneTime);
    const arrTotal = arrItems.reduce((sum, item) => sum + calculateItemTotal(item), 0);
    return (arrTotal / (employeeCount * 12)).toFixed(2);
  };

  // Render table for items
  const renderItemsTable = (itemsList) => (
    <table className="w-full">
      <thead>
        <tr className="border-b border-slate-100">
          <th className="text-left py-2 px-4 text-xs font-semibold text-slate-500 w-28">SKU</th>
          <th className="text-left py-2 px-4 text-xs font-semibold text-slate-500">Item</th>
          <th className="text-center py-2 px-4 text-xs font-semibold text-slate-500 w-14">Qty</th>
          <th className="text-right py-2 px-4 text-xs font-semibold text-slate-500 w-24">List</th>
          <th className="text-right py-2 px-4 text-xs font-semibold text-slate-500 w-28">Discount</th>
          <th className="text-right py-2 px-4 text-xs font-semibold text-slate-500 w-24">Final</th>
          {isHCMDeal && (
            <th className="text-right py-2 px-4 text-xs font-semibold text-purple-600 w-24">PEPM</th>
          )}
        </tr>
      </thead>
      <tbody>
        {itemsList.map((item, idx) => {
          const discount = calculateItemDiscount(item);
          const finalPrice = calculateItemTotal(item);
          const effectiveDiscount = getEffectiveDiscount(item);
          const itemPepm = calculateItemPEPM(item);

          return (
            <tr key={idx} className="border-b border-slate-50 hover:bg-slate-50">
              <td className="py-2 px-4 text-xs font-mono text-slate-500">{item.sku}</td>
              <td className="py-2 px-4 text-sm text-slate-800">
                {item.name}
                {item.isPEPM && (
                  <span className="ml-2 text-xs text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">
                    PEPM
                  </span>
                )}
                {item.isOneTime && (
                  <span className="ml-2 text-xs text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded">
                    One-time
                  </span>
                )}
              </td>
              <td className="py-2 px-4 text-sm text-center text-slate-600">{item.quantity}</td>
              <td className="py-2 px-4 text-sm text-right text-slate-700">
                ${(item.listPrice * item.quantity).toLocaleString()}
              </td>
              <td className="py-2 px-4 text-sm text-right text-red-600">
                {discount > 0 ? `-$${discount.toLocaleString()} (${effectiveDiscount}%)` : '-'}
              </td>
              <td className="py-2 px-4 text-sm text-right font-semibold text-slate-800">
                ${finalPrice.toLocaleString()}
              </td>
              {isHCMDeal && (
                <td className="py-2 px-4 text-sm text-right font-medium text-purple-600">
                  {itemPepm ? `$${itemPepm}` : '-'}
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  return (
    <div>
      <h2 className="text-2xl font-bold text-slate-800 mb-2">Final Review & Generate Quote</h2>
      <p className="text-slate-600 mb-8">Review final pricing and generate your quote</p>

      <div className="space-y-6">
        {/* Customer Info */}
        <div className="p-6 bg-slate-50 rounded-xl">
          <h3 className="font-semibold text-slate-800 mb-3">Customer Information</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-slate-600">Customer:</span>
              <span className="ml-2 font-medium text-slate-800">{quoteData.customerName || 'Not specified'}</span>
            </div>
            <div>
              <span className="text-slate-600">Industry:</span>
              <span className="ml-2 font-medium text-slate-800">{quoteData.industry || 'Not specified'}</span>
            </div>
            <div>
              <span className="text-slate-600">Region:</span>
              <span className="ml-2 font-medium text-slate-800">{quoteData.region}</span>
            </div>
            <div>
              <span className="text-slate-600">Opportunity ID:</span>
              <span className="ml-2 font-medium text-slate-800">{quoteData.opportunityId || 'Not specified'}</span>
            </div>
          </div>
        </div>

        {/* Grouped Line Items */}
        <div className="space-y-4">
          {Object.entries(categoryGroups).map(([groupKey, group]) => {
            const groupItems = groupedItems[groupKey];
            if (groupItems.length === 0) return null;

            const Icon = group.icon;
            const colors = colorClasses[group.color];
            const isExpanded = expandedGroups[groupKey];
            const discountedTotal = getGroupDiscountedTotal(groupKey);
            const totalDiscount = getGroupDiscount(groupKey);
            const hasSubCategories = group.subCategories;
            const groupPepm = getGroupPEPM(groupKey);

            return (
              <div key={groupKey} className={`rounded-xl border-2 ${colors.border} overflow-hidden`}>
                {/* Group Header */}
                <button
                  onClick={() => toggleGroup(groupKey)}
                  className={`w-full ${colors.bg} px-4 py-3 flex items-center justify-between hover:opacity-90`}
                >
                  <div className="flex items-center gap-3">
                    {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    <div className={`w-8 h-8 ${colors.icon} rounded-lg flex items-center justify-center`}>
                      <Icon size={16} className="text-white" />
                    </div>
                    <div className="text-left">
                      <h3 className={`font-semibold ${colors.text}`}>{group.label}</h3>
                      <span className="text-xs text-slate-500">{groupItems.length} items</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className={`font-bold ${colors.text}`}>
                        ${discountedTotal.toLocaleString()}
                      </div>
                      {totalDiscount > 0 && (
                        <div className="text-xs text-red-600">
                          -${totalDiscount.toLocaleString()} discount
                        </div>
                      )}
                    </div>
                    {isHCMDeal && groupPepm && (
                      <div className="text-sm text-purple-600 bg-purple-50 px-2 py-1 rounded">
                        PEPM: ${groupPepm}
                      </div>
                    )}
                  </div>
                </button>

                {/* Group Content */}
                {isExpanded && (
                  <div className="bg-white">
                    {hasSubCategories ? (
                      // Render sub-categories for Products
                      <div className="divide-y divide-slate-100">
                        {Object.entries(group.subCategories).map(([subKey, subCat]) => {
                          const subItems = getSubCategoryItems(subCat.categories);
                          if (subItems.length === 0) return null;

                          const isSubExpanded = expandedSubCategories[subKey];
                          const subListTotal = getSubCategoryListTotal(subCat.categories);
                          const subDiscountedTotal = getSubCategoryDiscountedTotal(subCat.categories);
                          const subDiscount = subListTotal - subDiscountedTotal;
                          const subPepm = getSubCategoryPEPM(subCat.categories);

                          return (
                            <div key={subKey}>
                              {/* Sub-category header */}
                              <button
                                onClick={() => toggleSubCategory(subKey)}
                                className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-slate-50 transition-colors"
                              >
                                <div className="flex items-center gap-2">
                                  <span className="w-5 h-5 flex items-center justify-center rounded bg-teal-100 text-teal-600">
                                    {isSubExpanded ? <Minus size={12} /> : <Plus size={12} />}
                                  </span>
                                  <span className="text-sm font-medium text-slate-700">{subCat.label}</span>
                                  <span className="text-xs text-slate-400">({subItems.length})</span>
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="text-sm font-semibold text-slate-700">
                                    ${subDiscountedTotal.toLocaleString()}
                                  </span>
                                  {subDiscount > 0 && (
                                    <span className="text-xs text-red-600">
                                      -${subDiscount.toLocaleString()}
                                    </span>
                                  )}
                                  {isHCMDeal && subPepm && (
                                    <span className="text-xs text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">
                                      PEPM: ${subPepm}
                                    </span>
                                  )}
                                </div>
                              </button>

                              {/* Sub-category items */}
                              {isSubExpanded && (
                                <div className="bg-slate-50/50">
                                  {renderItemsTable(subItems)}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      // Render regular items table
                      renderItemsTable(groupItems)
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Grand Total Summary */}
        <div className="bg-slate-800 rounded-xl p-6">
          <div className="space-y-3">
            {/* ARR Section */}
            <div className="flex items-center justify-between text-slate-300">
              <span>ARR (List Price)</span>
              <span className="font-semibold">${totals.arrSubtotal.toLocaleString()}</span>
            </div>
            {totals.arrDiscount > 0 && (
              <div className="flex items-center justify-between text-red-400">
                <span>ARR Discount</span>
                <span className="font-semibold">-${totals.arrDiscount.toLocaleString()}</span>
              </div>
            )}
            <div className="border-t border-slate-600 pt-3 flex items-center justify-between">
              <div>
                <div className="text-slate-400 text-sm">Total ARR</div>
                <div className="text-xs text-slate-500 mt-1">
                  {items.filter(i => !i.isOneTime).length} recurring items
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-3xl font-bold text-green-400">
                  ${totals.totalARR.toLocaleString()}
                </div>
                {isHCMDeal && (
                  <div className="text-sm text-purple-400 bg-purple-900/30 px-2 py-1 rounded">
                    PEPM: ${(totals.totalARR / (employeeCount * 12)).toFixed(2)}
                  </div>
                )}
              </div>
            </div>

            {/* One-Time Section */}
            {totals.oneTimeSubtotal > 0 && (
              <>
                <div className="border-t border-slate-600 pt-3 flex items-center justify-between text-orange-300">
                  <span>One-Time (List Price)</span>
                  <span className="font-semibold">${totals.oneTimeSubtotal.toLocaleString()}</span>
                </div>
                {totals.oneTimeDiscount > 0 && (
                  <div className="flex items-center justify-between text-red-400">
                    <span>One-Time Discount</span>
                    <span className="font-semibold">-${totals.oneTimeDiscount.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <div className="text-orange-400 text-sm">Total One-Time</div>
                  <div className="text-xl font-bold text-orange-400">
                    ${totals.totalOneTime.toLocaleString()}
                  </div>
                </div>
              </>
            )}

            {/* Grand Total */}
            {totals.oneTimeSubtotal > 0 && (
              <div className="border-t border-slate-600 pt-3 flex items-center justify-between">
                <div className="text-white text-sm font-semibold">Grand Total (ARR + One-Time)</div>
                <div className="text-xl font-bold text-white">
                  ${totals.total.toLocaleString()}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* PEPM Summary for HCM Deals */}
        {isHCMDeal && (() => {
            // Calculate PEPM using total ARR (after discounts)
            const totalARR = totals.totalARR;
            const pepm = totalARR / (employeeCount * 12);
            const formattedPepm = pepm.toFixed(2);

            // HCM Platform benchmark by employee tier
            let hcmBenchmark, tierName;
            if (employeeCount >= 10000) {
              hcmBenchmark = 15;
              tierName = 'Enterprise (10K+)';
            } else if (employeeCount >= 5000) {
              hcmBenchmark = 17; // Interpolated between enterprise and mid-market
              tierName = 'Large (5K-10K)';
            } else if (employeeCount >= 2000) {
              hcmBenchmark = 20;
              tierName = 'Mid-Market (2K-5K)';
            } else {
              hcmBenchmark = 24;
              tierName = 'SMB (<2K)';
            }

            // Max allowed is 20% of HCM platform PEPM
            const maxAllowedPepm = hcmBenchmark * 0.20;
            const isWithinThreshold = pepm <= maxAllowedPepm;
            const percentOfBenchmark = ((pepm / hcmBenchmark) * 100).toFixed(1);

            return (
              <div className={`bg-gradient-to-r ${isWithinThreshold ? 'from-green-50 to-blue-50 border-green-200' : 'from-red-50 to-orange-50 border-red-200'} border-2 rounded-xl p-6`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 ${isWithinThreshold ? 'bg-green-600' : 'bg-red-600'} rounded-lg flex items-center justify-center`}>
                      <TrendingUp size={20} className="text-white" />
                    </div>
                    <div>
                      <h3 className={`text-lg font-semibold ${isWithinThreshold ? 'text-green-900' : 'text-red-900'}`}>HCM PEPM Summary</h3>
                      <p className={`text-sm ${isWithinThreshold ? 'text-green-700' : 'text-red-700'}`}>Per Employee Per Month Calculation (After Discounts)</p>
                    </div>
                  </div>
                  {/* Status Badge */}
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${isWithinThreshold ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {isWithinThreshold ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
                    <span className="text-sm font-medium">
                      {isWithinThreshold ? 'Within Guideline' : 'Above Guideline'}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                  <div className="bg-white rounded-lg p-4 border border-slate-200">
                    <div className="text-sm text-slate-600 mb-1">Employee Count</div>
                    <div className="text-2xl font-bold text-purple-900">
                      {employeeCount.toLocaleString()}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">{tierName}</div>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-slate-200">
                    <div className="text-sm text-slate-600 mb-1">Total ARR</div>
                    <div className="text-2xl font-bold text-blue-900">
                      ${totalARR.toLocaleString()}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">after discounts</div>
                  </div>
                  <div className={`bg-white rounded-lg p-4 border-2 ${isWithinThreshold ? 'border-green-300' : 'border-red-300'}`}>
                    <div className="text-sm text-slate-600 mb-1">Your PEPM</div>
                    <div className={`text-3xl font-bold ${isWithinThreshold ? 'text-green-600' : 'text-red-600'}`}>
                      ${formattedPepm}
                    </div>
                    <div className={`text-xs mt-1 ${isWithinThreshold ? 'text-green-600' : 'text-red-600'}`}>
                      {percentOfBenchmark}% of HCM benchmark
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-slate-200">
                    <div className="text-sm text-slate-600 mb-1">Pricing Intelligence</div>
                    <div className="text-2xl font-bold text-slate-700">
                      ${maxAllowedPepm.toFixed(2)}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">20% of ${hcmBenchmark} benchmark (UKG, Workday, SuccessFactors)</div>
                  </div>
                </div>

                <div className={`rounded-lg p-4 ${isWithinThreshold ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  <div className={`text-xs font-semibold mb-2 ${isWithinThreshold ? 'text-green-800' : 'text-red-800'}`}>
                    {isWithinThreshold ? '✓ Pricing Validation Passed' : '⚠ Pricing Review Required'}
                  </div>
                  <div className="text-sm text-slate-700">
                    <span className="font-mono">${totalARR.toLocaleString()} ÷ ({employeeCount.toLocaleString()} × 12) = ${formattedPepm} PEPM</span>
                  </div>
                  <div className={`text-xs mt-2 ${isWithinThreshold ? 'text-green-700' : 'text-red-700'}`}>
                    {isWithinThreshold
                      ? `Your PEPM of $${formattedPepm} is within the 20% guideline ($${maxAllowedPepm.toFixed(2)}) of the ${tierName} HCM benchmark ($${hcmBenchmark} from UKG, Workday, SuccessFactors, etc.).`
                      : `Your PEPM of $${formattedPepm} exceeds the 20% guideline ($${maxAllowedPepm.toFixed(2)}) of the ${tierName} HCM benchmark ($${hcmBenchmark} from UKG, Workday, SuccessFactors, etc.). Consider adjusting pricing or discounts.`
                    }
                  </div>
                </div>
              </div>
            );
        })()}
      </div>
    </div>
  );
};

export default FinalReviewStep;
